import { workspace } from '../../shared/api';
import { get, pick } from 'lodash';
import { Cid, Exam, ExamSearch, FavoriteExam } from '../../interfaces/exam';

export const ExamService = {
  async searchExam({
    search,
    prescriptionId,
    doctorId,
  }: {
    search: string;
    prescriptionId: number;
    doctorId: number;
  }): Promise<{ exams: ExamSearch[]; searchExamLogId: number | null }> {
    const params = {
      query: search,
      idPrescricao: prescriptionId,
      idMedico: doctorId,
    };

    const { data, headers } = await workspace.get('/exame/busca', { params });

    return { exams: data.exames || data, searchExamLogId: get(headers, 'x-id-busca-exame', null) };
  },

  async sendClickEvent({ logId, examId }: { logId: number; examId: number }) {
    await workspace.post(`exame/busca/log/${logId}/event`, {
      event: 'click',
      examId: examId,
    });
  },

  async addExamToPrescription({
    prescriptionId,
    payload,
    searchExamLogId,
  }: {
    prescriptionId: number;
    payload: Exam;
    searchExamLogId: number | null;
  }): Promise<AddExamResponse> {
    const headers: Record<string, any> = {};

    if (searchExamLogId) {
      headers['x-id-busca-exame'] = searchExamLogId;
    }

    const { data } = await workspace.post(
      `/prescricao/${prescriptionId}/exame`,
      {
        ...pick(payload, [
          'AnestesiaRequerida',
          'CodigoTUSS',
          'Detalhe',
          'Label',
          'LadoRequerido',
          'Nome',
          'NomeTUSS',
          'Tipo',
          'idEzOrder',
          'Lateralidade',
          'Instrucoes',
          'AplicarAnestesia',
          'CID10Estruturado',
          'categoria',
          'idExterno',
          'sigtap',
          'tipoFormulario',
          'referencia',
          'ReferenciaExterna',
          'externalReference',
          'examId',
          'customExamIndex',
          'QuantidadeSessoes',
        ]),
      },
      {
        headers,
      }
    );
    return data;
  },

  removeExamFromPrescription(prescriptionExamId: number) {
    return workspace.delete(`/prescricao/exame/${prescriptionExamId}`);
  },

  updateExamFromPrescription(prescriptionExamId: number, exam: Exam) {
    const payload = pick(exam, [
      'Lateralidade',
      'AplicarAnestesia',
      'Instrucoes',
      'CID10Estruturado',
      'tipoFormulario',
      'QuandoRealizar',
      'DataEspecifica',
      'QuantidadeSessoes',
    ]);
    return workspace.put(`/prescricao/exame/${prescriptionExamId}`, payload);
  },
  updateGeneralDataExam(prescriptionExamId: number, payload: { CID10Estruturado?: Cid; Instrucoes: string }) {
    return workspace.put(`/prescricao/${prescriptionExamId}/exame/geral`, payload);
  },
  async getGeneralExamData(prescriptionExamId: number): Promise<{ CID10Estruturado: Cid; Instrucoes: string }> {
    const { data } = await workspace.get(`/prescricao/${prescriptionExamId}/exame/geral`);

    return data;
  },

  async getFavoriteExams(prescriberId: number, prescriptionId: number): Promise<FavoriteExam[]> {
    const { data } = await workspace.get(`/medico/${prescriberId}/favoritos/exames?prescriptionId=${prescriptionId}`);
    return data.exams;
  },

  async addFavoriteExam(prescriberId: number, examId: string, customExamIndex?: string | null) {
    const payload: any = { examId };
    if (customExamIndex) {
      payload.customExamIndex = customExamIndex;
    }
    await workspace.post(`/medico/${prescriberId}/favoritos/exames`, payload);
  },

  async removeFavoriteExam(prescriberId: number, examId: string) {
    await workspace.delete(`/medico/${prescriberId}/favoritos/exames/${examId}`);
  },
};

export interface AddExamResponse {
  AnestesiaRequerida: boolean;
  AplicarAnestesia: boolean;
  CodigoTUSS: string;
  Instrucoes: string;
  Label: string;
  LadoRequerido: boolean;
  Nome: string;
  idEzOrder: number;
  idPrescricaoExame: number;
  sigtap: string;
}
